import React, { useState, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
// v2 client: POST /v1/kg/{pid}/pulse/{patientid} exists only on the v2 gateway
// (gateway/api/pulse/pulse_chat.py). api.js would send it to v1 (it is not on
// v2's care client-routing allowlist) and v1 answers 404.
import careiqapi from "../../../api/careiqapi";
// BlinkScribe's transcription-only route (Voice Edit in the note editor uses
// the same one): the Pulse mic records locally and puts the words in the box.
import blinkscribeapi from "../../../api/blinkscribeapi";
// The Documents tab's viewer (free FHIR-mirror reads, local uploads, zoom).
import ProcedureDocument from "../tabs/Document/ProcedureDocument";
import "./PulseChat.css";
import { getPracticeId } from "../../../config/practice";
import { pulseActionAutoOk } from "./pulseRefTab";

// The Pulse endpoint for a patient of this practice (v2 gateway).
export const pulsePath = (patientId) => `/v1/kg/${getPracticeId()}/pulse/${patientId}`;

/* ── Starter prompts shown on first open ── */
const STARTERS = [
  { icon: "👋", text: "Summarize this patient" },
  { icon: "🔬", text: "Any abnormal labs?" },
  { icon: "📋", text: "What happened last visit?" },
  { icon: "💊", text: "Current medications?" },
];

/* ── Pulse avatar SVG ── */
const PulseAvatar = ({ size = 32 }) => (
  <svg width={size} height={size} viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="20" cy="20" r="20" fill="url(#pulseGrad)" />
    {/* Heartbeat / pulse line */}
    <path
      d="M8 21h5l2-5 3 10 3-8 2 3h5"
      stroke="#fff"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
      fill="none"
    />
    <defs>
      <linearGradient id="pulseGrad" x1="0" y1="0" x2="40" y2="40" gradientUnits="userSpaceOnUse">
        <stop stopColor="#E8653A" />
        <stop offset="1" stopColor="#F4A68A" />
      </linearGradient>
    </defs>
  </svg>
);

/* ── Typing dots animation ── */
const TypingDots = () => (
  <div className="pulse-typing">
    <span /><span /><span />
  </div>
);

/* ── "Pull up" actions (v2 pulse_actions.py) ── */
function actionIcon(type) {
  switch (type) {
    case "open_document": return "🩻";
    case "open_lab_trend": return "📈";
    default: return "↗";
  }
}

function actionText(a) {
  if (a.type === "open_document") return `Open ${a.label || "document"}`;
  if (a.type === "open_lab_trend") return `Show ${a.analyte || "lab"} trend`;
  return a.label || "Open";
}

// The viewer's `procedure` for an open_document action: the same id fields the
// Documents tab's list rows carry (a FHIR-only outside report opens by its
// resource id, as CareIQ's openReport does).
export function actionProcedure(a, patientId) {
  if (!a || a.type !== "open_document" || !a.documentid) return null;
  const base = { patientid: patientId, fhir_resource_id: a.resource_id || null };
  if (a.category === "clinicaldocument") {
    return { ...base, clinicaldocumentid: a.documentid, documentclass: "CLINICALDOCUMENT" };
  }
  if (a.category === "encounterdocument") {
    return { ...base, encounterdocumentid: a.documentid, documentclass: "ENCOUNTERDOCUMENT" };
  }
  return { ...base, imagingresultid: a.documentid };
}

// Mic support: a browser that can record (MediaRecorder + getUserMedia).
export function micSupported() {
  return typeof window !== "undefined" && typeof window.MediaRecorder === "function" &&
    typeof navigator !== "undefined" && !!navigator.mediaDevices &&
    typeof navigator.mediaDevices.getUserMedia === "function";
}

/* ── Single chat message bubble ── */
const MessageBubble = ({ msg, onRefClick, onAction }) => {
  const isUser = msg.role === "user";

  // Parse message content, splitting on [REF:{...}] tags to render them inline
  const renderContent = () => {
    if (!msg.content) return null;
    const refPattern = /\[REF:(\{[^}]+\})\]/g;
    const parts = [];
    let lastIndex = 0;
    let match;

    while ((match = refPattern.exec(msg.content)) !== null) {
      // Add text before this ref
      if (match.index > lastIndex) {
        const textBefore = msg.content.slice(lastIndex, match.index);
        parts.push(
          <span key={`t-${lastIndex}`} dangerouslySetInnerHTML={{ __html: formatMessage(textBefore) }} />
        );
      }
      // Add the inline ref chip
      try {
        const ref = JSON.parse(match[1]);
        parts.push(
          <button
            type="button"
            key={`r-${match.index}`}
            className={`pulse-ref-chip-inline pulse-ref-${ref.type}`}
            onClick={() => onRefClick(ref)}
            title={ref.date ? `${ref.label} (${ref.date})` : ref.label}
          >
            <span className="pulse-ref-icon">{refIcon(ref.type)}</span>
            <span className="pulse-ref-label">{ref.label}</span>
          </button>
        );
      } catch {
        // If JSON parse fails, just skip the tag
      }
      lastIndex = match.index + match[0].length;
    }

    // Add remaining text after last ref
    if (lastIndex < msg.content.length) {
      const remaining = msg.content.slice(lastIndex);
      parts.push(
        <span key={`t-${lastIndex}`} dangerouslySetInnerHTML={{ __html: formatMessage(remaining) }} />
      );
    }

    // If no refs found, just format the whole message
    if (parts.length === 0) {
      return <span dangerouslySetInnerHTML={{ __html: formatMessage(msg.content) }} />;
    }

    return parts;
  };

  return (
    <div className={`pulse-msg-row ${isUser ? "pulse-msg-user" : "pulse-msg-bot"}`}>
      {!isUser && (
        <div className="pulse-msg-avatar">
          <PulseAvatar size={26} />
        </div>
      )}
      <div className={`pulse-msg-bubble ${isUser ? "pulse-bubble-user" : "pulse-bubble-bot"}`}>
        <div className="pulse-msg-text">
          {renderContent()}
        </div>
        {!isUser && Array.isArray(msg.actions) && msg.actions.length > 0 && (
          <div className="pulse-actions" role="group" aria-label="Pulse actions">
            {msg.actions.map((a, i) => (
              <button
                type="button"
                key={`a-${i}`}
                className={`pulse-action-btn pulse-action-${a.type}`}
                onClick={() => onAction && onAction(a)}
                title={a.date ? `${actionText(a)} (${a.date})` : actionText(a)}
              >
                <span className="pulse-action-icon">{actionIcon(a.type)}</span>
                <span>{actionText(a)}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

/* ── Helpers ── */
function refIcon(type) {
  switch (type) {
    case "lab": return "🔬";
    case "encounter": return "📋";
    case "imaging": return "🩻";
    case "medication": return "💊";
    case "condition": return "🩺";
    default: return "📎";
  }
}

function formatMessage(text) {
  if (!text) return "";
  // Escape first: the reply is model output quoting chart text, injected as HTML.
  const safe = text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;").replace(/'/g, "&#39;");
  // Bold **text**
  let html = safe.replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>");
  // Bullet points
  html = html.replace(/^[-•]\s+(.+)$/gm, "<li>$1</li>");
  if (html.includes("<li>")) {
    html = html.replace(/(<li>.*?<\/li>)+/gs, (match) => `<ul>${match}</ul>`);
  }
  // Line breaks
  html = html.replace(/\n/g, "<br/>");
  return html;
}


/* ═══════════════════════════════════════════════════════
   PulseChat — main chat panel component
   ═══════════════════════════════════════════════════════ */
const PulseChat = ({ patientId, patientName, onClose, onRefClick, onAction, careiqN3 }) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [greeted, setGreeted] = useState(false);
  // The document an open_document action opened (Documents tab's viewer).
  const [docView, setDocView] = useState(null);
  // Mic: "idle" | "recording" | "transcribing"; micNote = a short status line.
  const [micState, setMicState] = useState("idle");
  const [micNote, setMicNote] = useState("");
  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const unmountedRef = useRef(false);
  const showMic = micSupported();

  // Stop the mic (and drop its audio) when the panel closes mid-recording.
  useEffect(() => () => {
    unmountedRef.current = true;
    const r = recorderRef.current;
    if (r) {
      try { r.onstop = null; if (r.state !== "inactive") r.stop(); } catch (_) { /* already stopped */ }
      try { r.stream?.getTracks().forEach((t) => t.stop()); } catch (_) { /* no tracks */ }
      recorderRef.current = null;
    }
  }, []);

  // Run a "pull up" action: a document opens here in the Documents tab's
  // viewer (the chart also moves to Documents behind it); a lab trend or a tab
  // goes to the chart (patientchartwrap: Labs focused on the analyte, or the tab).
  const runAction = useCallback((a) => {
    if (!a) return;
    const proc = actionProcedure(a, patientId);
    if (proc) setDocView(proc);
    if (onAction) onAction(a);
  }, [patientId, onAction]);

  // Auto-scroll to bottom
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, loading]);

  // Focus input on mount
  useEffect(() => {
    if (inputRef.current) inputRef.current.focus();
  }, []);

  const sendMessage = useCallback(async (text) => {
    if (!text.trim() || loading) return;

    const userMsg = { role: "user", content: text.trim() };
    const newMessages = [...messages, userMsg];
    setMessages(newMessages);
    setInput("");
    setLoading(true);

    try {
      // Build history (exclude current message)
      const history = newMessages.slice(0, -1).map((m) => ({
        role: m.role,
        content: m.content,
      }));

      // careiqapi returns the body: { reply, references } (reply keeps its
      // [REF:{...}] tags, rendered inline as citation chips).
      const res = await careiqapi.post(pulsePath(patientId), {
        message: text.trim(),
        history,
      });

      const actions = res.actions || res.data?.actions || [];
      const botMsg = {
        role: "assistant",
        content: res.reply || res.data?.reply || "I couldn't generate a response.",
        references: res.references || res.data?.references || [],
        actions: Array.isArray(actions) ? actions : [],
      };

      setMessages((prev) => [...prev, botMsg]);
      if (!greeted) setGreeted(true);
      // "Show her last thyroid ultrasound": the provider asked to see it, so
      // the first action runs now (v2 sets auto only on an explicit request).
      // auto-run only when this chart lists the action's tab (no Documents tab: the button stays, no auto-open)
      if (botMsg.actions[0]?.auto === true && pulseActionAutoOk(botMsg.actions[0], careiqN3)) runAction(botMsg.actions[0]);
    } catch (err) {
      console.error("Pulse error:", err?.response?.status || err?.message);
      // 403: the patient is outside the user's department/provider scope.
      const outOfScope = err?.response?.status === 403;
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: outOfScope
            ? "This patient is outside your department/provider scope, so I can't review this chart."
            : "Sorry, I ran into an issue. Please try again.",
          references: [],
        },
      ]);
    } finally {
      setLoading(false);
    }
  }, [messages, loading, patientId, greeted, runAction, careiqN3]);

  // Mic -> BlinkScribe transcribe-only -> the input box (not sent: the
  // provider reads it, edits if needed, presses Enter).
  const startMic = useCallback(async () => {
    if (micState !== "idle") return;
    setMicNote("");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      if (unmountedRef.current) { stream.getTracks().forEach((t) => t.stop()); return; }
      chunksRef.current = [];
      let rec;
      try { rec = new window.MediaRecorder(stream, { mimeType: "audio/webm" }); }
      catch (_) { rec = new window.MediaRecorder(stream); }
      rec.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunksRef.current.push(e.data); };
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        recorderRef.current = null;
        const blob = new Blob(chunksRef.current, { type: rec.mimeType || "audio/webm" });
        if (blob.size < 1000) { setMicState("idle"); setMicNote("Didn't catch that — try again."); return; }
        setMicState("transcribing");
        try {
          const formData = new FormData();
          formData.append("file", blob, "pulse_question.webm");
          const r = await blinkscribeapi.post("/v1/recording/transcribe-only", formData, {
            headers: { "Content-Type": "multipart/form-data" },
            timeout: 60000,
          });
          const text = (r?.transcript || r?.data?.transcript || "").trim();
          if (unmountedRef.current) return;
          if (text) {
            setInput((prev) => (prev.trim() ? `${prev.trim()} ${text}` : text));
            setMicNote("");
            if (inputRef.current) inputRef.current.focus();
          } else {
            setMicNote("Couldn't transcribe — try again or type.");
          }
        } catch (err) {
          console.error("Pulse mic transcribe failed:", err?.response?.status || err?.message);
          if (!unmountedRef.current) setMicNote("Couldn't transcribe — try again or type.");
        } finally {
          if (!unmountedRef.current) setMicState("idle");
        }
      };
      rec.start();
      recorderRef.current = rec;
      setMicState("recording");
    } catch (err) {
      console.error("Pulse mic access failed:", err?.message);
      setMicState("idle");
      setMicNote("Microphone not available.");
    }
  }, [micState]);

  const stopMic = useCallback(() => {
    const r = recorderRef.current;
    if (r && r.state !== "inactive") r.stop();
  }, []);

  // Escape closes the panel from anywhere inside it.
  const handlePanelKeyDown = (e) => {
    if (e.key === "Escape") {
      e.stopPropagation();
      onClose();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage(input);
    }
  };

  const panel = (
    <div
      className="pulse-panel"
      id="pulse-panel"
      role="dialog"
      aria-label="Pulse chart assistant"
      onKeyDown={handlePanelKeyDown}
    >
      {/* Header */}
      <div className="pulse-header">
        <div className="pulse-header-left">
          <PulseAvatar size={28} />
          <div>
            <div className="pulse-header-title">Pulse</div>
            <div className="pulse-header-sub">
              {patientName ? `Reviewing ${patientName}'s chart` : "Chart Assistant"}
            </div>
          </div>
        </div>
        <button type="button" className="pulse-close-btn" onClick={onClose} title="Close Pulse" aria-label="Close Pulse">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M18 6 6 18" /><path d="m6 6 12 12" />
          </svg>
        </button>
      </div>

      {/* Messages area */}
      <div className="pulse-messages" ref={scrollRef}>
        {messages.length === 0 && !loading && (
          <div className="pulse-welcome">
            <PulseAvatar size={48} />
            <div className="pulse-welcome-title">Hi, I'm Pulse</div>
            <div className="pulse-welcome-sub">
              Ask me anything about this patient's chart.
            </div>
            <div className="pulse-starters">
              {STARTERS.map((s, i) => (
                <button
                  type="button"
                  key={i}
                  className="pulse-starter-btn"
                  onClick={() => sendMessage(s.text)}
                >
                  <span>{s.icon}</span> {s.text}
                </button>
              ))}
            </div>
          </div>
        )}

        {messages.map((msg, i) => (
          <MessageBubble key={i} msg={msg} onRefClick={onRefClick} onAction={runAction} />
        ))}

        {loading && (
          <div className="pulse-msg-row pulse-msg-bot">
            <div className="pulse-msg-avatar">
              <PulseAvatar size={26} />
            </div>
            <div className="pulse-msg-bubble pulse-bubble-bot">
              <TypingDots />
            </div>
          </div>
        )}
      </div>

      {/* Input area */}
      <div className="pulse-input-area">
        <div className="pulse-input-wrap">
          <textarea
            ref={inputRef}
            className="pulse-input"
            placeholder="Ask about this patient..."
            aria-label="Ask Pulse about this patient"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            rows={1}
            disabled={loading}
          />
          {showMic && (
            <button
              type="button"
              className={`pulse-mic-btn${micState === "recording" ? " pulse-mic-on" : ""}`}
              aria-label={micState === "recording" ? "Stop dictation" : "Dictate a question"}
              aria-pressed={micState === "recording"}
              title={micState === "recording" ? "Stop and transcribe" : micState === "transcribing" ? "Transcribing…" : "Dictate a question"}
              onClick={micState === "recording" ? stopMic : startMic}
              disabled={loading || micState === "transcribing"}
            >
              {micState === "transcribing" ? (
                <span className="pulse-mic-spin" aria-hidden="true" />
              ) : micState === "recording" ? (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><rect x="6" y="6" width="12" height="12" rx="2" /></svg>
              ) : (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <rect x="9" y="2" width="6" height="12" rx="3" /><path d="M5 10v1a7 7 0 0 0 14 0v-1" /><path d="M12 18v4" />
                </svg>
              )}
            </button>
          )}
          <button
            type="button"
            className="pulse-send-btn"
            aria-label="Send"
            onClick={() => sendMessage(input)}
            disabled={!input.trim() || loading}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="m22 2-7 20-4-9-9-4z" /><path d="m22 2-11 11" />
            </svg>
          </button>
        </div>
        <div className="pulse-input-hint" aria-live="polite">
          {micState === "recording" ? "Listening… tap ■ to stop"
            : micState === "transcribing" ? "Transcribing…"
              : micNote || "Press Enter to send"}
        </div>
      </div>
    </div>
  );

  // The document viewer sits above the panel (the panel is z 9998 and clips
  // with overflow hidden, so the viewer gets its own layer on <body>).
  const viewer = docView ? (
    <div className="pulse-doc-layer">
      <ProcedureDocument
        subview={docView.imagingresultid ? 1 : 3}
        isProcedureDocumentOpen
        setIsProcedureDocumentOpen={(open) => { if (!open) setDocView(null); }}
        procedure={docView}
      />
    </div>
  ) : null;
  const canPortal = typeof document !== "undefined" && document.body;

  // On <body>: the chart window (pmodal) clips with overflow-hidden and may sit
  // in a transformed subtree, which would re-anchor position: fixed.
  return canPortal ? (
    <>
      {createPortal(panel, document.body)}
      {viewer && createPortal(viewer, document.body)}
    </>
  ) : (
    <>
      {panel}
      {viewer}
    </>
  );
};

export { PulseAvatar };
export default PulseChat;
